import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Header, Footer } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Loader2, CheckCircle2, Sparkles, ArrowRight, Star } from 'lucide-react';
import { API } from '../utils/authAxios';
import { toast } from 'sonner';

const TITLE = 'Comparte tu testimonio · Reto 21 Días | SinAdicciones.org';

export default function TestimonioReto21Page() {
  const [params] = useSearchParams();
  const token = params.get('t') || '';

  const [loading, setLoading] = useState(true);
  const [prefill, setPrefill] = useState(null);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    texto: '',
    nombre_display: '',
    mostrar_region: true,
    mostrar_edad: true,
    permite_publicar: true,
    rating: 0,
  });

  useEffect(() => {
    document.title = TITLE;
  }, []);

  useEffect(() => {
    if (!token) {
      setError('Link inválido. Asegúrate de abrir este enlace desde el email del Reto 21.');
      setLoading(false);
      return;
    }
    axios.get(API + '/reto21/testimonio/prefill', { params: { t: token } })
      .then((r) => {
        setPrefill(r.data);
        setForm((f) => ({ ...f, nombre_display: (r.data.nombre || '').split(' ')[0] || '' }));
        if (r.data.ya_enviado) setSubmitted(true);
      })
      .catch(() => setError('Link inválido o expirado.'))
      .finally(() => setLoading(false));
  }, [token]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (form.texto.trim().length < 30) {
      toast.error('Cuéntanos un poco más (mínimo 30 caracteres).');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(API + '/reto21/testimonio', {
        token,
        texto: form.texto.trim(),
        nombre_display: form.nombre_display.trim(),
        mostrar_region: !!form.mostrar_region,
        mostrar_edad: !!form.mostrar_edad,
        permite_publicar: !!form.permite_publicar,
        rating: form.rating || null,
      });
      setSubmitted(true);
      toast.success('¡Gracias por compartir!');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No pudimos enviar tu testimonio.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950">
      <Header />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 pb-16">
        {loading ? (
          <div className="flex justify-center py-32"><Loader2 className="w-8 h-8 animate-spin text-emerald-500" /></div>
        ) : error ? (
          <Card className="bg-slate-900/60 border-slate-800" data-testid="error-card">
            <CardContent className="p-10 text-center">
              <p className="text-slate-300 text-lg">{error}</p>
            </CardContent>
          </Card>
        ) : submitted ? (
          <Card className="bg-gradient-to-br from-emerald-950/40 to-slate-900/60 border-emerald-700/40" data-testid="thanks-card">
            <CardContent className="p-10 text-center">
              <CheckCircle2 className="w-14 h-14 text-emerald-400 mx-auto mb-4" />
              <h2 className="text-3xl font-bold text-white mb-3">¡Gracias, {prefill?.nombre?.split(' ')[0] || 'amigo/a'}!</h2>
              <p className="text-slate-300 text-base max-w-xl mx-auto">
                Tu testimonio quedó registrado. Revisaremos rápidamente que se vea bien — y si elegiste permitir publicación, lo verás pronto en el sitio.
              </p>
              <div className="mt-8 flex flex-wrap gap-3 justify-center">
                <Link to="/reto-21-dias">
                  <Button variant="outline" className="border-slate-700 text-slate-200 hover:bg-slate-800" data-testid="back-reto">Volver al Reto 21</Button>
                </Link>
                <Link to="/cotizar">
                  <Button className="bg-emerald-500 hover:bg-emerald-600" data-testid="cta-cotizar">Cotizar tratamiento <ArrowRight className="w-4 h-4 ml-2" /></Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        ) : (
          <>
            <div className="mb-8">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-500/10 border border-amber-500/30 text-amber-300 text-xs font-medium mb-3" data-testid="badge-day21">
                <Sparkles className="w-3.5 h-3.5" /> Día 21 · Lo lograste
              </div>
              <h1 className="text-3xl sm:text-4xl font-bold text-white">Comparte tu testimonio</h1>
              <p className="text-slate-300 mt-3">
                Lo que escribas puede ser la chispa que otra persona necesita para empezar su día 1. Tú decides cómo apareces y si lo publicamos.
              </p>
            </div>

            <Card className="bg-slate-900/60 border-slate-800" data-testid="testimonio-form">
              <CardContent className="p-6 sm:p-8 space-y-5">
                {/* Rating */}
                <div>
                  <label className="text-sm text-slate-200 font-medium mb-2 block">¿Cuántas estrellas le darías al Reto 21?</label>
                  <div className="flex gap-1.5" data-testid="rating-stars">
                    {[1, 2, 3, 4, 5].map((n) => (
                      <button
                        key={n}
                        type="button"
                        onClick={() => set('rating', n)}
                        className="transition-transform hover:scale-110"
                        data-testid={`star-${n}`}
                        aria-label={`${n} estrellas`}
                      >
                        <Star
                          className={`w-7 h-7 ${form.rating >= n ? 'fill-amber-400 text-amber-400' : 'text-slate-600'}`}
                        />
                      </button>
                    ))}
                  </div>
                </div>

                {/* Texto */}
                <div>
                  <label className="text-sm text-slate-200 font-medium mb-2 block">Tu testimonio</label>
                  <textarea
                    rows={6}
                    value={form.texto}
                    onChange={(e) => set('texto', e.target.value)}
                    placeholder="¿Qué te llevas del Reto 21? ¿Qué fue lo más difícil? ¿Qué le dirías a alguien que está pensando en empezar?"
                    className="w-full px-4 py-3 bg-slate-800/60 border border-slate-700 rounded-lg text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm leading-relaxed"
                    maxLength={1500}
                    data-testid="input-texto"
                  />
                  <p className="text-xs text-slate-500 mt-1">{form.texto.length}/1500 caracteres · mínimo 30</p>
                </div>

                {/* Cómo aparece */}
                <div>
                  <label className="text-sm text-slate-200 font-medium mb-2 block">¿Cómo quieres aparecer?</label>
                  <input
                    type="text"
                    value={form.nombre_display}
                    onChange={(e) => set('nombre_display', e.target.value.slice(0, 60))}
                    placeholder="Tu nombre o un alias (deja vacío para 'Anónimo')"
                    className="w-full px-4 py-2.5 bg-slate-800/60 border border-slate-700 rounded-lg text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm"
                    data-testid="input-nombre-display"
                  />
                  <p className="text-xs text-slate-500 mt-1">Vista previa: <span className="text-slate-300 font-medium">{form.nombre_display.trim() || 'Anónimo'}</span></p>
                </div>

                {/* Checks */}
                <div className="space-y-3 pt-2">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.mostrar_edad}
                      onChange={(e) => set('mostrar_edad', e.target.checked)}
                      className="mt-1 w-4 h-4 accent-emerald-500"
                      data-testid="check-mostrar-edad"
                    />
                    <span className="text-sm text-slate-300">
                      Mostrar mi edad <span className="text-slate-500">({prefill?.edad || '—'} años)</span>
                    </span>
                  </label>
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.mostrar_region}
                      onChange={(e) => set('mostrar_region', e.target.checked)}
                      className="mt-1 w-4 h-4 accent-emerald-500"
                      data-testid="check-mostrar-region"
                    />
                    <span className="text-sm text-slate-300">
                      Mostrar mi región <span className="text-slate-500">({prefill?.region || '—'})</span>
                    </span>
                  </label>
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.permite_publicar}
                      onChange={(e) => set('permite_publicar', e.target.checked)}
                      className="mt-1 w-4 h-4 accent-emerald-500"
                      data-testid="check-permite-publicar"
                    />
                    <span className="text-sm text-slate-300">
                      Permito que SinAdicciones.org publique mi testimonio (revisado por un equipo humano antes).
                    </span>
                  </label>
                </div>

                <div className="pt-4 border-t border-slate-800">
                  <Button
                    onClick={submit}
                    disabled={submitting || form.texto.trim().length < 30}
                    size="lg"
                    className="w-full bg-amber-500 hover:bg-amber-600 text-black font-semibold"
                    data-testid="submit-btn"
                  >
                    {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                    Enviar testimonio
                  </Button>
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </main>
      <Footer />
    </div>
  );
}
